import React, { useState, useContext, useEffect } from "react";
import { STATUS_MESSAGE_COLORS, BORDERS } from "../../../config";

import { CoreContext } from "../../../context/global/CoreContext";
import { TimekeepingContext } from "../../../context/domain/timekeeping/TimekeepingContext";

import { editShiftData, saveShiftData } from "../../../services/api/timekeeping";
import { getJobNumbers } from "../../../services/api/core";
import { useMessageDebounced } from "../../../hooks/useMessageDebounced";

import ShiftSummary from "./infoBoxes/ShiftSummary";
import { Button } from "../../../components/ui";

/**
 * EditShift Component
 *
 * Simplified version that provides functionality for users to edit a single work shift.
 *
 * @param {Object} props - Component props
 * @param {Object} props.shiftData - The shift data objects to edit
 * @param {Function} props.onClose - Function to call when closing the editor
 * @returns {JSX.Element} The EditShift component UI
 */
const EditShift = ({ shiftDataIn, dataValidationFileds, onShiftChange }) => {
  const { scopesList } = useContext(CoreContext);
  const { fetchWeeklyShifts } = useContext(TimekeepingContext);
  // Using local message state instead of context-based message state
  // Due to the fact multiple EditShift components can be rendered on the same page
  // With different states and messages.
  const { message, setMessageDebounced } = useMessageDebounced();
  // Component state
  const [isLoading, setIsLoading] = useState(false);
  const [shiftData, setShiftData] = useState(shiftDataIn || {});
  const [hasSaved, setHasSaved] = useState(false);
  // Fetch jobs for the specific date of the shift (not just today)
  const [jobsList, setJobsList] = useState([]);
  
  // Fetch jobs for the shift's date whenever shiftData or date changes
  useEffect(() => {
    const fetchJobsForDate = async () => {
      // Get date from shiftData.date or shiftData.shiftDate
      const shiftDate = shiftData.date || shiftData.shiftDate;
      if (shiftDate) {
        try {
          const jobs = await getJobNumbers(true, shiftDate);
          setJobsList(jobs);
        } catch (error) {
          console.error('Error fetching jobs for date:', error);
          setJobsList([]);
        }
      } else {
        // If no date in shift data, fetch for today as fallback
        try {
          const jobs = await getJobNumbers(true);
          setJobsList(jobs);
        } catch (error) {
          console.error('Error fetching jobs:', error);
          setJobsList([]);
        }
      }
    };
    fetchJobsForDate();
  }, [shiftData.date, shiftData.shiftDate]);

  // Handle field changes
  const handleFieldChange = (field, value) => {
    const updatedShift = {
      ...shiftData,
      [field]: value,
    };
    setShiftData(updatedShift);

    // Notify parent component about changes if callback provided
    if (onShiftChange && typeof onShiftChange === "function") {
      onShiftChange(updatedShift);
    }
  };

  const handleUpdateShift = async () => {
    setIsLoading(true);

    if (!shiftData.id) {
      // If no ID, this is a new shift being created
      const result = await saveShiftData(shiftData);
      if (result.success) {
        setMessageDebounced({ type: "success", text: result.message });
        setIsLoading(false);
        setHasSaved(true);
      } else {
        setMessageDebounced({
          type: "error",
          text: result.message || "Save shift failed",
        });
        setIsLoading(false);
      }
      return;
    }

    // Validate inputs
    if (
      !shiftData.job ||
      !shiftData.workScope ||
      !shiftData.startTime ||
      !shiftData.finishTime
    ) {
      setMessageDebounced({
        type: "error",
        text: "Please fill in all required fields",
      });
      setIsLoading(false);
      return;
    }

    if (shiftData.startTime >= shiftData.finishTime) {
      setMessageDebounced({
        type: "error",
        text: "Start time must be before finish time",
      });
      setIsLoading(false);
      return;
    }

    fetchWeeklyShifts(); // Update to fetch the latest shifts

    // Fall back to using dataValidationFileds if available
    if (dataValidationFileds && dataValidationFileds.length > 0) {
      for (const field of dataValidationFileds) {
        if (field.prevTimeOut) {
          // Check if start time is after previous shift's finish time
          if (shiftData.startTime < field.prevTimeOut) {
            setMessageDebounced({
              type: "error",
              text: "Start time must be after the previous shift's finish time",
            });
            setIsLoading(false);
            return;
          }
        }
        if (field.nextTimeIn) {
          // Check if finish time is before next shift's start time
          if (shiftData.finishTime > field.nextTimeIn) {
            setMessageDebounced({
              type: "error",
              text: "Finish time must be before the next shift's start time",
            });
            setIsLoading(false);
            return;
          }
        }
      }
    }

    try {
      const result = await editShiftData(shiftData);

      if (result.success) {
        setMessageDebounced({ type: "success", text: result.message });
        setIsLoading(false);
        setHasSaved(true);

        // Notify parent of successful save
        if (onShiftChange && typeof onShiftChange === "function") {
          onShiftChange(result.shift || shiftData, true); // Pass the saved shift and success flag
        }
        // fetchWeeklyShifts(); // Refresh weekly shifts after edit
      } else {
        setMessageDebounced({
          type: "error",
          text: result.message || "Edit shift failed",
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error updating shift:", error);
      setMessageDebounced({
        type: "error",
        text: "Failed to update shift. Please try again.",
      });
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-3 ${BORDERS.radius.md} ${
            message.type === "success"
              ? STATUS_MESSAGE_COLORS.success
              : STATUS_MESSAGE_COLORS.error
          }`}
        >
          {message.text}
        </div>
      )}
      {hasSaved ? (
        <ShiftSummary
          shift={shiftData}
          removeFields={["overtime", "regularTime"]}
        />
      ) : (
        // Single Shift Editor
        <div>
          {shiftData && Object.keys(shiftData).length > 0 ? (
            <div className="mb-4 rounded-t-md">
              {/* Header */}
              <div
                className={`${shiftData.stage === "Missed Shift" ? STATUS_MESSAGE_COLORS.error : STATUS_MESSAGE_COLORS.success} p-4 flex justify-between items-center ${BORDERS.radius.md} rounded-t-md`}
              >
                <h3 className="font-bold">
                  Shift
                  {shiftData.stage && ` (${shiftData.stage})`}
                </h3>
              </div>

              {/* Content */}
              <div className="p-4 bg-gray-100 rounded-b-md">
                {/* Job */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job
                  </label>
                  <div className="relative">
                    <select
                      value={shiftData.job || ""}
                      onChange={(e) => handleFieldChange("job", e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoading}
                    >
                      <option value="">Select a Job</option>
                      {jobsList.length > 0 ? (
                        jobsList.map((job) => (
                          <option key={job.id} value={job.id}>
                            {job.number} - {job.description}
                          </option>
                        ))
                      ) : (
                        <option value="">No jobs available</option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Work Scope */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Scope
                  </label>
                  <div className="relative">
                    <select
                      value={shiftData.workScope || ""}
                      onChange={(e) =>
                        handleFieldChange("workScope", e.target.value)
                      }
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoading}
                    >
                      <option value="">Select a Scope</option>
                      {scopesList?.length > 0 ? (
                        scopesList.map((scope) => (
                          <option key={scope.id} value={scope.id}>
                            {scope.title}
                          </option>
                        ))
                      ) : (
                        <option value="">
                          No scopes, please contact your super.
                        </option>
                      )}
                    </select>
                  </div>
                </div>

                {/* Work Description */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Description
                  </label>
                  <textarea
                    value={shiftData.workDescription || ""}
                    onChange={(e) =>
                      handleFieldChange("workDescription", e.target.value)
                    }
                    placeholder="Describe your work activities here..."
                    rows={3}
                    className="block caret-black w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    disabled={isLoading}
                  ></textarea>
                </div>

                {/* Legally Required Breaks */}
                <div className="mb-4">
                  <label className="flex items-center text-sm font-medium text-gray-700">
                    <input
                      type="checkbox"
                      checked={shiftData.breaksCompleted || false}
                      onChange={(e) =>
                        handleFieldChange("breaksCompleted", e.target.checked)
                      }
                      className="mr-3 h-4 ml-2 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      disabled={isLoading}
                    />
                    Legally required breaks taken
                  </label>
                </div>

                {/* Time fields */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Time In */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time In
                    </label>
                    <input
                      type="time"
                      value={shiftData.startTime || ""}
                      onChange={(e) =>
                        handleFieldChange("startTime", e.target.value)
                      }
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoading}
                    />
                  </div>

                  {/* Time Out */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time Out
                    </label>
                    <input
                      type="time"
                      value={shiftData.finishTime || ""}
                      onChange={(e) =>
                        handleFieldChange("finishTime", e.target.value)
                      }
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-base focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <Button
                  variant="primary"
                  text="Save Shift"
                  onClick={() => handleUpdateShift()}
                  className="py-1 px-4"
                  icon={isLoading ? "spinner" : null}
                  disabled={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="bg-gray-100 p-6 text-center rounded-lg">
              <p>No shift data provided.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EditShift;
